import React, { useCallback, useState } from 'react';
import cn from 'classnames';

import validateEmail from 'bl/validation/validateEmail.bl';
import validateName from 'bl/validation/validateName.bl';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import isEmptyObject from 'utils/predicates/object/isEmptyObject.util';

import InputField from 'onboarding/common/InputField';

import CN from './ClientAccountForm.module.scss';

const ACTION = Object.freeze({
  updateCustomer: 'update_customer',
  updateCard: 'update_card',
});

const ClientAccountForm = ({
  beUpdateSalonBillingData,
  handleGoBack,
  isNew,
  onChange,
  salonId,
  setClientSecret,
  setShowAccountForm,
  stripeEmail,
  stripeName,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const [name, setName] = useState(fromAnyToString(stripeName));
  const [email, setEmail] = useState(fromAnyToString(stripeEmail));
  const [isNameValid, setIsNameValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false);

  const isNextEnabled = fromAnyToBool(name && email && !isNameValid && !isEmailValid);

  const handleName = (name) => void setName(name);
  const handleEmail = (email) => void setEmail(email);

  const handleValidateName = useCallback(
    (value) => {
      const valid = validateName({ value });
      setIsNameValid(!valid);
      return valid ? '' : t('common.errors.validation.noName');
    },
    [t],
  );

  const handleValidateEmail = useCallback(
    (value) => {
      const valid = validateEmail({ value });
      setIsEmailValid(!valid);
      return valid ? '' : t('common.errors.validation.notValidEmail');
    },
    [t],
  );

  const handleClick = useCallback(async () => {
    const { updateCustomer, updateCard } = ACTION;
    beUpdateSalonBillingData({ salonId, name, email, action: updateCustomer })
      .then((result) => {
        const { response, error } = result;
        if (response && response.client_secret) {
          const clientSecret = response.client_secret;
          setClientSecret(clientSecret);
          setShowModal(true);
          return;
        }

        if (isNew) {
          // eslint-disable-next-line promise/no-nesting
          beUpdateSalonBillingData({ salonId, name, email, action: updateCard }).then((result) => {
            const { response } = result;
            if (response && response.client_secret) {
              const clientSecret = response.client_secret;
              setClientSecret(clientSecret);
              setShowModal(true);
            }
          });
        }

        if (isEmptyObject(response)) {
          setShowModal(true);
        }

        if (error) {
          setErrorMessage(t('common.errors.wrongResponse'));
        }
      })
      .catch(() => setErrorMessage(t('common.errors.generic')));
  }, [beUpdateSalonBillingData, email, isNew, name, salonId, setClientSecret, t]);

  const handleModal = useCallback(() => {
    setShowModal(false);
    if (isNew) {
      setShowAccountForm(false);
      return;
    }
    onChange(true);
  }, [onChange, isNew, setShowAccountForm]);
  const handleBack = useCallback(() => handleGoBack(), [handleGoBack]);

  return (
    <div data-bem="ClientAccountForm">
      <div
        data-bem="ClientAccountForm"
        className={cn({
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        <div className={CN.section}>
          <div className={CN.inner}>
            <div className={CN.subTitle}>{t('staffAndBusiness.subAndBilling.billingDetails.section1Title')}</div>
            <div className={CN.container}>
              <div className={CN.inputContainer}>
                <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.accountNameLabel')}</div>
                <InputField
                  data={{
                    id: 'NameInput',
                    inputValue: name,
                    placeholder: t('staffAndBusiness.subAndBilling.billingDetails.accountNamePlaceholder'),
                    type: 'text',
                  }}
                  onChangeHandler={handleName}
                  validateBlur={handleValidateName}
                />
              </div>
              <div className={CN.inputContainer}>
                <div className={CN.label}>{t('staffAndBusiness.subAndBilling.billingDetails.accountEmailLabel')}</div>

                <InputField
                  data={{
                    id: 'EmailInput',
                    inputValue: email,
                    placeholder: t('staffAndBusiness.subAndBilling.billingDetails.accountEmailPlaceholder'),
                    type: 'text',
                  }}
                  onChangeHandler={handleEmail}
                  validateBlur={handleValidateEmail}
                />
              </div>
            </div>
            <div className={CN.buttonContainer}>
              {!isNew && (
                <Button onClick={handleBack} variant="outline">
                  <div className={CN.buttonGap}>{t('common.buttons.cancelUpp')}</div>
                </Button>
              )}
              <Button disabled={!isNextEnabled} onClick={handleClick} variant="primary">
                <div className={CN.buttonGap}>
                  {isNew ? t('common.buttons.addUpp') : t('common.buttons.saveChanges')}
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {errorMessage && <p className="errorMessage">{errorMessage}</p>}

      <SalonModal tag="ClientAccountForm" isOpen={showModal} withFooter>
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={handleModal}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus variant="success">
            <Icon variant="confirm" width="lg" />
          </IconStatus>
          <div className={CN.contentModal}>
            <div className={CN.titleModal}>
              {isNew
                ? t('staffAndBusiness.subAndBilling.billingDetails.successModalMsg1')
                : t('staffAndBusiness.subAndBilling.billingDetails.successModalMsg2')}
            </div>
          </div>
          <div className={CN.textModal}>
            {isNew && t('staffAndBusiness.subAndBilling.billingDetails.successModalText')}
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <Button tag="ClientAccountForm__button-next" onClick={handleModal} variant="primary" noMargin xSpace>
            {t('common.buttons.next')}
          </Button>
        </SalonModal.Footer>
      </SalonModal>
    </div>
  );
};

export default ClientAccountForm;
